import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Trash2, UserX } from "lucide-react";

export const RemovePatientButton = ({ patient, token, onRemoved, label = "Remove patient" }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const h = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  useEffect(() => {
    if (!open) { setPreview(null); setName(""); return; }
    fetch(`${API}/staff/patients/${patient.user_id}/removal-preview`, { headers: h })
      .then(async r => { const d = await r.json().catch(() => ({})); setPreview(r.ok ? d : { error: d.detail || "Could not load what will be deleted. Please try again." }); })
      .catch(() => setPreview({ error: "Connection error. Please try again." }));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/staff/patients/${patient.user_id}/remove`, { method: "POST", headers: h, body: JSON.stringify({ confirm_name: name }) });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Could not remove patient"); return; }
      toast.success(`${patient.name} removed (${d.total} records deleted)`);
      setOpen(false);
      onRemoved ? onRemoved() : navigate("/admin/patients");
    } catch { toast.error("Connection error"); } finally { setBusy(false); }
  };
  const expected = (patient.name || patient.email || "").trim();
  const matches = !!name.trim() && name.trim().toLowerCase() === expected.toLowerCase();

  return (<>
    <Button variant="outline" size="sm" className="text-xs rounded-lg text-red-600 border-red-200 hover:bg-red-50" onClick={() => setOpen(true)} data-testid={onRemoved ? `remove-account-${patient.user_id}` : "remove-patient-btn"}><UserX className="w-3.5 h-3.5 mr-1" />{label}</Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="remove-patient-dialog">
        <DialogHeader>
          <DialogTitle>Remove {patient.name}?</DialogTitle>
          <DialogDescription>This permanently deletes this account and all its records. It can't be undone.</DialogDescription>
        </DialogHeader>
        {!preview ? <Loader2 className="w-5 h-5 animate-spin text-stone-400" /> : preview.error ? (
          <p className="text-sm text-red-600" data-testid="remove-patient-error">{preview.error}</p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-stone-700" data-testid="remove-patient-total"><b>{preview.total}</b> records will be deleted:</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-xs text-stone-600 max-h-48 overflow-y-auto">
              {Object.entries(preview.counts || {}).map(([c, n]) => <div key={c} className="flex justify-between"><span className="capitalize">{c.replace(/_/g, " ")}</span><b>{n}</b></div>)}
            </div>
            <p className="text-xs text-stone-600">Type <b>{expected}</b> to confirm.</p>
            <Input value={name} onChange={e => setName(e.target.value)} className="rounded-lg" data-testid="remove-patient-confirm" />
            <Button onClick={remove} disabled={!matches || busy} className="w-full bg-red-600 hover:bg-red-700 text-white rounded-lg" data-testid="remove-patient-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}Remove permanently
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  </>);
};
